import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatINR } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, AreaChart, Area, XAxis, YAxis, CartesianGrid } from "recharts";
import {
  Sparkles, ArrowRight, ArrowLeft, ShieldCheck, TrendingUp, Info,
  Loader2, ChevronDown, RotateCcw, Wallet, Plus, Check, SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#6366F1", "#0F172A", "#10B981", "#F59E0B", "#38BDF8"];

const AMOUNTS = [25000, 50000, 100000, 250000];
const GOALS = [
  { v: "Grow my wealth over time", d: "Long-term growth" },
  { v: "Save for a specific goal", d: "House, car, wedding" },
  { v: "Build an emergency fund", d: "Safety cushion" },
  { v: "Save tax", d: "80C / ELSS options" },
];
const TIMELINES = ["Under 1 year", "1-3 years", "3-5 years", "5+ years"];
const RISKS = [
  { v: "low", t: "Play it safe", d: "I prefer stability over high returns" },
  { v: "medium", t: "Balanced", d: "Some ups and downs are okay" },
  { v: "high", t: "Go for growth", d: "I can handle bigger swings" },
];

function RiskBadge({ level }) {
  const map = {
    "Lower risk": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Moderate risk": "bg-amber-50 text-amber-700 border-amber-200",
    "Higher risk": "bg-orange-50 text-orange-700 border-orange-200",
  };
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${map[level] || map["Moderate risk"]}`}>
      {level}
    </span>
  );
}

function RecCard({ rec, index, color }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      className="rounded-3xl border border-slate-200 bg-white p-6 soft-shadow"
      data-testid="rec-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-10 w-10 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${color}1a` }}>
            <span className="h-3 w-3 rounded-full" style={{ background: color }} />
          </span>
          <div className="min-w-0">
            <p className="font-heading font-semibold text-lg text-slate-900 truncate">{rec.instrument}</p>
            <p className="text-sm text-slate-400">{rec.category}</p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="font-mono font-bold text-xl text-slate-900">{formatINR(rec.amount)}</p>
          <p className="text-xs text-slate-400">{rec.allocation_pct}% of plan</p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <RiskBadge level={rec.risk_level} />
        <span className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
          <ShieldCheck size={14} className="text-indigo-500" />
          Confidence {rec.confidence}%
        </span>
      </div>

      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden mb-4">
        <motion.div className="h-full rounded-full" style={{ background: color }}
          initial={{ width: 0 }} animate={{ width: `${rec.confidence}%` }} transition={{ delay: index * 0.08 + 0.3, duration: 0.8 }} />
      </div>

      <p className="text-sm text-slate-600 leading-relaxed">{rec.why}</p>

      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`explain-simply-${index}`}
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700 transition-colors duration-200"
      >
        <Info size={15} /> Explain simply
        <ChevronDown size={15} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-3 space-y-3 rounded-2xl bg-slate-50 p-4 text-sm">
              <p className="text-slate-700"><span className="font-semibold">In simple words: </span>{rec.explain_simply}</p>
              {rec.historically && <p className="text-slate-500"><span className="font-semibold text-slate-700">Historically: </span>{rec.historically}</p>}
              <p className="text-orange-700"><span className="font-semibold">Potential downside: </span>{rec.potential_downside}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

const RATES = { low: [0.06, 0.08], medium: [0.09, 0.12], high: [0.11, 0.15] };
const YEAR_CHIPS = [1, 3, 5, 10, 15];

function ScenarioProjection({ projection, risk }) {
  const [amount, setAmount] = useState(projection.invested);
  const [years, setYears] = useState(projection.years);
  const [lo, hi] = RATES[(risk || "").toLowerCase()] || RATES.medium;

  const { points, endLow, endHigh } = useMemo(() => {
    const amt = Number(amount) || 0;
    const pts = [];
    for (let y = 0; y <= years; y++) {
      pts.push({ year: y, low: Math.round(amt * Math.pow(1 + lo, y)), high: Math.round(amt * Math.pow(1 + hi, y)) });
    }
    return { points: pts, endLow: pts[pts.length - 1].low, endHigh: pts[pts.length - 1].high };
  }, [amount, years, lo, hi]);

  const dirty = Number(amount) !== projection.invested || years !== projection.years;
  const reset = () => { setAmount(projection.invested); setYears(projection.years); };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8 soft-shadow mb-6" data-testid="projection-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-500 mb-1">Projected value</p>
          <h3 className="font-heading font-semibold text-xl text-slate-900">
            In {years} {years === 1 ? "year" : "years"}, an estimated
          </h3>
        </div>
        {dirty && (
          <button onClick={reset} data-testid="scenario-reset"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors duration-200">
            <RotateCcw size={15} /> Reset
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-baseline gap-2 mb-1">
        <span className="font-mono font-bold text-3xl text-slate-900" data-testid="projection-range">
          {formatINR(endLow)} – {formatINR(endHigh)}
        </span>
      </div>
      <p className="text-sm text-slate-400 mb-6">
        on {formatINR(Number(amount) || 0)} invested today · assumes an illustrative {Math.round(lo * 100)}–{Math.round(hi * 100)}% per year range for a {risk} risk mix
      </p>

      {/* Scenario controls */}
      <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4 mb-6" data-testid="scenario-controls">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-1.5">
          <SlidersHorizontal size={14} className="text-indigo-500" /> Try a different scenario
        </p>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-600">Amount</label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm font-mono text-slate-400">₹</span>
                <Input type="number" data-testid="scenario-amount-input" value={amount}
                  onChange={(e) => setAmount(e.target.value === "" ? "" : Number(e.target.value))}
                  className="h-9 w-32 pl-6 font-mono text-right rounded-xl" />
              </div>
            </div>
            <Slider data-testid="scenario-amount-slider" min={1000} max={1000000} step={5000}
              value={[Math.min(1000000, Number(amount) || 0)]} onValueChange={(v) => setAmount(v[0])} className="mt-3" />
            <div className="flex justify-between text-[11px] text-slate-400 mt-1.5"><span>₹1k</span><span>₹10L</span></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-600">Time horizon</label>
              <span className="text-sm font-mono font-semibold text-slate-900">{years} {years === 1 ? "yr" : "yrs"}</span>
            </div>
            <Slider data-testid="scenario-years-slider" min={1} max={25} step={1}
              value={[years]} onValueChange={(v) => setYears(v[0])} className="mt-3" />
            <div className="flex flex-wrap gap-1.5 mt-3">
              {YEAR_CHIPS.map((y) => (
                <button key={y} data-testid={`scenario-year-${y}`} onClick={() => setYears(y)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors duration-200 ${
                    years === y ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                  }`}>{y}y</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={230}>
        <AreaChart data={points} margin={{ left: -6, right: 8, top: 8 }}>
          <defs>
            <linearGradient id="pHigh" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6366F1" stopOpacity={0.28} />
              <stop offset="100%" stopColor="#6366F1" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
          <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false}
            tickFormatter={(y) => (y === 0 ? "Now" : `${y}y`)} />
          <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false}
            tickFormatter={(v) => `₹${(v / 100000).toFixed(1)}L`} />
          <Tooltip formatter={(v, n) => [formatINR(v), n === "high" ? "Optimistic" : "Conservative"]}
            labelFormatter={(y) => (y === 0 ? "Today" : `After ${y} year${y > 1 ? "s" : ""}`)} />
          <Area type="monotone" dataKey="high" name="high" stroke="#6366F1" strokeWidth={2.5} fill="url(#pHigh)" isAnimationActive={false} />
          <Area type="monotone" dataKey="low" name="low" stroke="#94A3B8" strokeWidth={2} strokeDasharray="5 4" fill="none" isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>

      <div className="flex items-center gap-5 mt-3 text-xs">
        <span className="flex items-center gap-1.5 text-slate-500"><span className="h-2.5 w-2.5 rounded-full bg-indigo-500" /> Optimistic range</span>
        <span className="flex items-center gap-1.5 text-slate-500"><span className="h-2.5 w-4 border-t-2 border-dashed border-slate-400" /> Conservative range</span>
      </div>
      <p className="text-xs text-slate-500 mt-4 leading-relaxed">{projection.note}</p>
    </div>
  );
}


export default function InvestPlan() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ amount: "", goal: "", timeline: "", risk_comfort: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [adopting, setAdopting] = useState(false);
  const [adopted, setAdopted] = useState(false);

  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const typeFromCategory = (c) => {
    const s = (c || "").toLowerCase();
    if (s.includes("gold")) return "Gold";
    if (s.includes("ppf")) return "PPF";
    if (s.includes("fixed") || s.includes("deposit") || s.includes(" fd")) return "FD";
    if (s.includes("bond") || s.includes("g-sec") || s.includes("govern")) return "Bonds";
    if (s.includes("stock") || (s.includes("equit") && s.includes("share"))) return "Stocks";
    return "Mutual Fund";
  };

  const adoptToPortfolio = async () => {
    if (!plan?.recommendations?.length) return;
    setAdopting(true);
    try {
      const items = plan.recommendations.map((r) => ({
        name: r.instrument,
        type: typeFromCategory(r.category),
        invested_amount: r.amount,
        current_value: r.amount,
        monthly_contribution: 0,
        start_date: null,
      }));
      await api.post("/investments/bulk", { items });
      setAdopted(true);
      toast.success("Added to your Portfolio", { description: "You can update current values anytime." });
    } catch (err) {
      toast.error("Couldn't add to portfolio. Please try again.");
    } finally {
      setAdopting(false);
    }
  };

  const steps = [
    {
      title: "How much would you like to invest?",
      sub: "Enter any amount you're comfortable with.",
      valid: () => Number(answers.amount) > 0,
      body: (
        <div>
          <div className="relative">
            <span className="absolute left-5 top-1/2 -translate-y-1/2 text-2xl font-mono text-slate-400">₹</span>
            <Input
              type="number"
              data-testid="plan-amount"
              value={answers.amount}
              onChange={(e) => set("amount", e.target.value)}
              placeholder="50,000"
              className="h-16 pl-11 text-2xl font-mono rounded-2xl border-slate-200"
              autoFocus
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {AMOUNTS.map((a) => (
              <button key={a} data-testid={`amount-chip-${a}`} onClick={() => set("amount", String(a))}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-200 ${
                  String(a) === answers.amount ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                }`}>
                {formatINR(a)}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: "What's this money for?",
      sub: "This helps us match the right options.",
      valid: () => !!answers.goal,
      body: (
        <div className="grid sm:grid-cols-2 gap-3">
          {GOALS.map((g) => (
            <button key={g.v} data-testid={`goal-${g.v}`} onClick={() => set("goal", g.v)}
              className={`text-left p-5 rounded-2xl border transition-colors duration-200 ${
                answers.goal === g.v ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300"
              }`}>
              <p className="font-semibold text-slate-900">{g.v}</p>
              <p className="text-sm text-slate-400 mt-0.5">{g.d}</p>
            </button>
          ))}
        </div>
      ),
    },
    {
      title: "When might you need it?",
      sub: "Your time horizon shapes the strategy.",
      valid: () => !!answers.timeline,
      body: (
        <div className="grid grid-cols-2 gap-3">
          {TIMELINES.map((t) => (
            <button key={t} data-testid={`timeline-${t}`} onClick={() => set("timeline", t)}
              className={`p-5 rounded-2xl border font-semibold transition-colors duration-200 ${
                answers.timeline === t ? "border-indigo-500 bg-indigo-50 text-slate-900" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}>
              {t}
            </button>
          ))}
        </div>
      ),
    },
    {
      title: "How do you feel about risk?",
      sub: "There are no wrong answers here.",
      valid: () => !!answers.risk_comfort,
      body: (
        <div className="space-y-3">
          {RISKS.map((r) => (
            <button key={r.v} data-testid={`risk-${r.v}`} onClick={() => set("risk_comfort", r.v)}
              className={`w-full text-left p-5 rounded-2xl border transition-colors duration-200 ${
                answers.risk_comfort === r.v ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300"
              }`}>
              <p className="font-semibold text-slate-900">{r.t}</p>
              <p className="text-sm text-slate-400 mt-0.5">{r.d}</p>
            </button>
          ))}
          <Textarea data-testid="plan-notes" value={answers.notes} onChange={(e) => set("notes", e.target.value)}
            placeholder="Anything else we should know? (optional)" className="rounded-2xl border-slate-200 mt-2" rows={2} />
        </div>
      ),
    },
  ];

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/invest/plan", {
        amount: Number(answers.amount),
        goal: answers.goal,
        timeline: answers.timeline,
        risk_comfort: answers.risk_comfort,
        notes: answers.notes,
      });
      setPlan(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't generate a plan. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setPlan(null); setStep(0); setAdopted(false); setAnswers({ amount: "", goal: "", timeline: "", risk_comfort: "", notes: "" }); };

  // ---------- Loading ----------
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.4, ease: "linear" }}>
          <Sparkles size={40} className="text-indigo-500" />
        </motion.div>
        <h2 className="font-heading font-semibold text-2xl text-slate-900 mt-6">Researching your options…</h2>
        <p className="text-slate-500 mt-2 max-w-sm">Comparing mutual funds, bonds, gold, FDs and ETFs against your goal and risk comfort.</p>
      </div>
    );
  }

  // ---------- Result ----------
  if (plan) {
    const allocation = plan.recommendations.map((r) => ({ name: r.instrument, value: r.amount }));
    return (
      <div className="p-6 md:p-10 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-500 mb-1">Your plan</p>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">
              {formatINR(plan.amount)} · {plan.goal}
            </h1>
          </div>
          <Button variant="outline" onClick={reset} data-testid="plan-restart" className="rounded-full gap-2 hidden sm:flex">
            <RotateCcw size={16} /> New plan
          </Button>
        </div>

        {/* Plan → Portfolio */}
        <div className="rounded-3xl border border-indigo-200 bg-indigo-50 p-5 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="adopt-banner">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-2xl bg-white flex items-center justify-center shrink-0">
              <TrendingUp size={20} className="text-indigo-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">Track this plan in your Portfolio</p>
              <p className="text-sm text-slate-500">Add all {plan.recommendations.length} holdings in one tap and watch them grow.</p>
            </div>
          </div>
          <Button
            onClick={adoptToPortfolio}
            disabled={adopting || adopted}
            data-testid="adopt-plan-btn"
            className={`rounded-full gap-2 shrink-0 ${adopted ? "bg-emerald-600 hover:bg-emerald-600" : "bg-slate-900 hover:bg-slate-800"}`}
          >
            {adopted ? <><Check size={17} /> Added to Portfolio</>
              : adopting ? <><Loader2 size={17} className="animate-spin" /> Adding…</>
              : <><Plus size={17} /> Add all to Portfolio</>}
          </Button>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8 soft-shadow mb-6">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 text-sm font-semibold mb-4">
                <ShieldCheck size={16} /> Overall confidence {plan.overall_confidence}%
              </div>
              <p className="text-slate-700 leading-relaxed" data-testid="plan-summary">{plan.summary}</p>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={allocation} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {allocation.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatINR(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="space-y-4 mb-6">
          {plan.recommendations.map((rec, i) => (
            <RecCard key={i} rec={rec} index={i} color={COLORS[i % COLORS.length]} />
          ))}
        </div>

        {plan.projection && (
          <ScenarioProjection projection={plan.projection} risk={plan.risk_comfort} />
        )}

        {plan.risks?.length > 0 && (
          <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 mb-6">
            <p className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Info size={18} className="text-amber-600" /> Things to keep in mind
            </p>
            <ul className="space-y-2">
              {plan.risks.map((r, i) => (
                <li key={i} className="text-sm text-slate-600 flex gap-2"><span className="text-amber-500 mt-1">•</span>{r}</li>
              ))}
            </ul>
          </div>
        )}

        <p className="text-xs text-slate-400 text-center leading-relaxed">
          {plan.disclaimer || "Educational information only, not registered investment advice. No investment is guaranteed."}
        </p>
        <Button variant="outline" onClick={reset} className="rounded-full gap-2 w-full mt-6 sm:hidden">
          <RotateCcw size={16} /> Start a new plan
        </Button>
      </div>
    );
  }

  // ---------- Questionnaire ----------
  const s = steps[step];
  return (
    <div className="min-h-screen flex flex-col">
      <div className="p-6 md:p-10 max-w-2xl mx-auto w-full flex-1 flex flex-col">
        {/* progress */}
        <div className="flex items-center gap-2 mb-10">
          {steps.map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i <= step ? "bg-indigo-500" : "bg-slate-200"}`} />
          ))}
        </div>

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-500 mb-2">
          Step {step + 1} of {steps.length}
        </p>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">{s.title}</h1>
            <p className="text-slate-500 mt-2 mb-8">{s.sub}</p>
            {s.body}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center gap-3 mt-auto pt-10">
          {step > 0 && (
            <Button variant="outline" onClick={() => setStep((x) => x - 1)} data-testid="plan-back" className="rounded-full h-12 px-5 gap-2">
              <ArrowLeft size={18} /> Back
            </Button>
          )}
          {step < steps.length - 1 ? (
            <Button onClick={() => setStep((x) => x + 1)} disabled={!s.valid()} data-testid="plan-next"
              className="rounded-full h-12 px-7 gap-2 bg-slate-900 hover:bg-slate-800 flex-1 sm:flex-none">
              Continue <ArrowRight size={18} />
            </Button>
          ) : (
            <Button onClick={generate} disabled={!s.valid()} data-testid="plan-generate"
              className="rounded-full h-12 px-7 gap-2 bg-indigo-600 hover:bg-indigo-700 flex-1 sm:flex-none">
              <Sparkles size={18} /> Get my plan
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
